import { drawing as d } from '@progress/kendo-drawing';
import { isString } from './../utils';
import { END, MarkerType, START } from '../constants';
import { deepExtend } from './../utils/deep-extend';
import { CircleMarker } from './CircleMarker';
import { ArrowMarker } from './ArrowMarker';
/**
 * Mixin that provides marker functionality for path-based elements.
 * Can be mixed into classes to add start and end marker capabilities.
 */
export const MarkerPathMixin = {
    /**
     * @hidden
     * Gets the path element for marker positioning.
     * @param position - The marker position (start or end)
     * @returns The path element or undefined
     */
    _getPath: function (position) {
        let path = this.drawingElement;
        if (path instanceof d.MultiPath) {
            if (position === START) {
                path = path.paths[0];
            }
            else {
                path = path.paths[path.paths.length - 1];
            }
        }
        if (path && path.segments.length) {
            return path;
        }
    },
    /**
     * @hidden
     * Normalizes marker options by converting string values to objects.
     * @param options - The options to normalize
     */
    _normalizeMarkerOptions: function (options) {
        const startCap = options.startCap;
        const endCap = options.endCap;
        if (isString(startCap)) {
            options.startCap = {
                type: startCap
            };
        }
        if (isString(endCap)) {
            options.endCap = {
                type: endCap
            };
        }
    },
    /**
     * @hidden
     * Removes a marker from the specified position.
     * @param position - The marker position (start or end)
     */
    _removeMarker: function (position) {
        const marker = this._markers[position];
        if (marker) {
            this.drawingContainer().remove(marker.drawingElement);
            delete this._markers[position];
        }
    },
    /**
     * @hidden
     * Creates markers for both start and end positions.
     */
    _createMarkers: function () {
        const options = this.options;
        this._normalizeMarkerOptions(options);
        this._markers = {};
        this._markers[START] = this._createMarker(options.startCap, START);
        this._markers[END] = this._createMarker(options.endCap, END);
    },
    /**
     * @hidden
     * Creates a marker for the specified position.
     * @param options - Marker configuration options
     * @param position - The marker position (start or end)
     * @returns The created marker instance
     */
    _createMarker: function (options, position) {
        const type = (options || {}).type;
        const path = this._getPath(position);
        let markerType, marker;
        if (!path) {
            this._removeMarker(position);
            return;
        }
        if (type === MarkerType.FilledCircle) {
            markerType = CircleMarker;
        }
        else if (type === MarkerType.ArrowStart || type === MarkerType.ArrowEnd) {
            markerType = ArrowMarker;
        }
        else {
            this._removeMarker(position);
        }
        if (markerType) {
            marker = new markerType(deepExtend({}, options, {
                position: position
            }));
            marker.positionMarker(path);
            this.drawingContainer().append(marker.drawingElement);
            return marker;
        }
    },
    /**
     * @hidden
     * Positions a marker at the specified position along the path.
     * @param position - The marker position (start or end)
     */
    _positionMarker: function (position) {
        const marker = this._markers[position];
        if (marker) {
            const path = this._getPath(position);
            if (path) {
                marker.positionMarker(path);
            }
            else {
                this._removeMarker(position);
            }
        }
    },
    /**
     * @hidden
     * Mapping of position names to cap option names.
     */
    _capMap: {
        start: 'startCap',
        end: 'endCap'
    },
    /**
     * @hidden
     * Redraws a marker at the specified position with updated options.
     * @param pathChange - Whether the path has changed
     * @param position - The marker position (start or end)
     * @param options - Updated marker options
     * @returns True if a new marker was created, false otherwise
     */
    _redrawMarker: function (pathChange, position, options) {
        this._normalizeMarkerOptions(options);
        const pathOptions = this.options;
        const cap = this._capMap[position];
        const pathCapType = (pathOptions[cap] || {}).type;
        const optionsCap = options[cap];
        let created = false;
        if (optionsCap) {
            pathOptions[cap] = deepExtend({}, pathOptions[cap], optionsCap);
            if (optionsCap.type && pathCapType !== optionsCap.type) {
                this._removeMarker(position);
                this._markers[position] = this._createMarker(pathOptions[cap], position);
                created = true;
            }
            else if (this._markers[position]) {
                this._markers[position].redraw(optionsCap);
            }
        }
        else if (pathChange && !this._markers[position] && pathOptions[cap]) {
            this._markers[position] = this._createMarker(pathOptions[cap], position);
            created = true;
        }
        return created;
    },
    /**
     * @hidden
     * Redraws all markers with updated options.
     * @param pathChange - Whether the path has changed
     * @param options - Updated marker options
     */
    _redrawMarkers: function (pathChange, options) {
        if (!this._redrawMarker(pathChange, START, options) && pathChange) {
            this._positionMarker(START);
        }
        if (!this._redrawMarker(pathChange, END, options) && pathChange) {
            this._positionMarker(END);
        }
    }
};
